import { ArrowRight, MapPin } from "lucide-react";
import { FACILITY, DIRECTIONS_URL } from "@/lib/location";

const COACH_IMG = "/images/coach-dakota.jpg";
const Over = ({ children }) => <p className="text-sm font-bold tracking-[0.25em] uppercase text-[#2563EB]">{children}</p>;
const H2 = ({ children, testid }) => <h2 data-testid={testid} className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tighter uppercase text-[#0B0F19] leading-[0.95]">{children}</h2>;

const PILLARS = [
  ["01", "Purpose over volume", "Every workout targets specific skills and outcomes. Athletes should understand what they're working on, why it matters, and how to improve."],
  ["02", "Competition with feedback", "We challenge athletes to execute under pressure, evaluate the results, and make adjustments. Competition creates opportunities to learn."],
  ["03", "Habits that transfer", "Better preparation, stronger routines, and personal accountability create development that extends beyond a single workout."],
];

export function WhyTrainHere({ onViewTraining }) {
  return (
    <section data-testid="why-train-here" className="bg-white py-20 md:py-28 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        <H2 testid="why-heading">Why train here?</H2>
        <p className="mt-5 max-w-2xl text-lg md:text-xl font-semibold text-slate-500">More repetitions aren't always better. Better repetitions have a purpose.</p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {PILLARS.map(([n, t, d]) => (
            <div key={n} data-testid={`pillar-${n}`} className="rounded-3xl border border-slate-200 p-6 md:p-7">
              <p className="text-sm font-extrabold text-[#2563EB]">{n}</p>
              <h3 className="mt-3 text-xl font-extrabold tracking-tight uppercase text-[#0B0F19]">{t}</h3>
              <p className="mt-3 text-base font-medium text-slate-500 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <p data-testid="why-closing" className="text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-[#0B0F19]">We provide the challenge. <span className="text-[#2563EB]">You solve the problem.</span></p>
          <button data-testid="why-view-training-btn" onClick={onViewTraining} className="group inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1d4fd7] text-white text-base font-bold px-8 py-4 rounded-full transition-colors shrink-0">
            View Training Options <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </section>
  );
}

const CREDENTIALS = [
  ["University of Kansas Baseball", "Outfielder & Pitcher"],
  ["Boston Red Sox Organization", "Professional Pitcher"],
  ["Strength Training Certified", ""],
];

export function MeetCoach() {
  return (
    <section data-testid="meet-coach" className="bg-white py-20 md:py-28 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-6 md:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <div className="aspect-[2/3] w-full max-w-sm mx-auto lg:mx-0 overflow-hidden rounded-3xl bg-slate-100">
            <img data-testid="coach-photo" src={COACH_IMG} alt="Dakota Smith fielding a ground ball for the University of Kansas" className="w-full h-full object-cover object-top" />
          </div>
        </div>
        <div className="lg:col-span-7 max-w-2xl">
          <Over>Meet Your Coach</Over>
          <H2 testid="coach-name">Dakota Smith</H2>
          <p className="mt-3 text-sm font-bold uppercase tracking-[0.2em] text-slate-400">Founder &amp; Coach | Rep Lab</p>
          <ul data-testid="coach-credentials" className="mt-7 border-t border-slate-200">
            {CREDENTIALS.map(([a, b]) => (
              <li key={a} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 py-3 border-b border-slate-200">
                <span className="text-base font-bold text-[#0B0F19]">{a}</span>
                {b && <span className="text-sm font-semibold text-slate-500">{b}</span>}
              </li>
            ))}
          </ul>
          <div className="mt-7 space-y-4 text-base md:text-lg font-medium text-slate-500 leading-relaxed">
            <p>Before starting Rep Lab, I played baseball at the University of Kansas as an outfielder and pitcher before continuing my career as a professional pitcher in the Boston Red Sox organization.</p>
            <p>The coaches who made the biggest impact on my career taught more than mechanics. They taught me how to prepare, develop routines, take care of my body, and understand what it takes to improve.</p>
            <p>That's the approach behind Rep Lab. We teach athletes how to train with purpose, make adjustments, embrace competition, and take ownership of their development.</p>
          </div>
          <blockquote data-testid="coach-quote" className="mt-8 border-l-4 border-[#2563EB] pl-5 text-xl sm:text-2xl font-extrabold italic tracking-tight text-[#0B0F19] leading-snug">
            "Results matter. But habits and routines determine success."
          </blockquote>
          <a data-testid="coach-instagram-link" href="https://www.instagram.com/replabbaseball/" target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-[#2563EB] transition-colors">
            Follow Rep Lab on Instagram <span className="text-[#2563EB]">@replabbaseball</span> <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}

export function TrainingLocation() {
  return (
    <section data-testid="training-location" className="bg-white py-16 md:py-20 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-6 md:px-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <Over>Training Location</Over>
          <p data-testid="location-name" className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tighter uppercase text-[#0B0F19]">{FACILITY.name}</p>
          <p data-testid="location-address" className="mt-2 text-lg font-semibold text-slate-500">{FACILITY.street}<br />{FACILITY.cityLine}</p>
        </div>
        <a data-testid="location-directions-link" href={DIRECTIONS_URL} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#0B0F19] px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-[#0B0F19] hover:bg-[#0B0F19] hover:text-white transition-colors self-start md:self-auto">
          <MapPin size={16} /> Get Directions
        </a>
      </div>
    </section>
  );
}
